import { useEffect, useRef, useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Download, Share2, ChevronLeft, ChevronRight, Check, Flag } from 'lucide-react'
import { sharePhoto, savePhoto, canShareFiles, buildFilename } from '@/lib/share'
import ReportSheet from './ReportSheet'

export interface ViewerPhoto {
  id: string
  type: string
  url: string
  downloadUrl: string
  sessionId: string
}

interface Props {
  photos: ViewerPhoto[]
  index: number
  eventId: string
  eventName: string | null
  onIndexChange: (i: number) => void
  onClose: () => void
}

/**
 * Full-screen, mobile-first photo viewer.
 *
 * Built around what a guest actually does at an event: open the photo big,
 * flick through the set, and get it into Instagram/WhatsApp within seconds.
 * Hence swipe navigation, a native share sheet where available, and actions
 * pinned to the bottom where a thumb can reach them.
 *
 * Guest-facing text is hardcoded in Dutch, this page has no language
 * switcher and every guest at these events is a Dutch speaker.
 */
export default function PhotoViewer({ photos, index, eventId, eventName, onIndexChange, onClose }: Props) {
  const [busy, setBusy] = useState<'share' | 'save' | null>(null)
  const [saved, setSaved] = useState(false)
  const [zoomed, setZoomed] = useState(false)

  const [reportOpen, setReportOpen] = useState(false)

  const touchStart = useRef<{ x: number; y: number } | null>(null)
  const photo = photos[index]

  const goTo = useCallback(
    (next: number) => {
      if (next < 0 || next >= photos.length) return
      setZoomed(false)
      onIndexChange(next)
    },
    [photos.length, onIndexChange]
  )

  // Keyboard support for anyone opening the gallery on a laptop.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') (reportOpen ? setReportOpen(false) : onClose())
      if (e.key === 'ArrowLeft') goTo(index - 1)
      if (e.key === 'ArrowRight') goTo(index + 1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [index, goTo, onClose, reportOpen])

  // Lock background scrolling while the viewer owns the screen.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  // Close the report sheet whenever the photo underneath it changes, it would
  // otherwise be left pointing at a photo the guest has already swiped past.
  useEffect(() => {
    setReportOpen(false)
  }, [photo?.id])

  const filename = buildFilename(eventName, photo?.downloadUrl || '', index + 1)

  const handleShare = async () => {
    if (!photo || busy) return
    setBusy('share')
    const result = await sharePhoto(photo.downloadUrl, filename, eventName || 'Mijn foto’s')
    // Browsers without file-share support fall through to a normal save so the
    // button always does something useful.
    if (result === 'unsupported') await savePhoto(photo.downloadUrl, filename)
    setBusy(null)
  }

  const handleSave = async () => {
    if (!photo || busy) return
    setBusy('save')
    await savePhoto(photo.downloadUrl, filename)
    setBusy(null)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const onTouchStart = (e: React.TouchEvent) => {
    if (zoomed || reportOpen) return
    const t = e.touches[0]
    touchStart.current = { x: t.clientX, y: t.clientY }
  }

  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart.current || zoomed || reportOpen) return
    const t = e.changedTouches[0]
    const dx = t.clientX - touchStart.current.x
    const dy = t.clientY - touchStart.current.y
    touchStart.current = null

    // Mostly-horizontal flick = navigate. Mostly-vertical downward = dismiss,
    // matching the gesture people already know from their photo apps.
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
      goTo(dx < 0 ? index + 1 : index - 1)
    } else if (dy > 90 && Math.abs(dx) < 80) {
      onClose()
    }
  }

  if (!photo) return null

  const isVideo = photo.type === 'video'

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-black" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Top bar: counter + report + close. Padded for notches/status bars. */}
      <div
        className="flex shrink-0 items-center justify-between px-4 py-3"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <span className="rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-white/70 tabular-nums">
          {index + 1} / {photos.length}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setReportOpen(true)}
            aria-label="Foto melden"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white/70 active:bg-white/25 transition-colors"
          >
            <Flag className="h-4.5 w-4.5" />
          </button>
          <button
            onClick={onClose}
            aria-label="Sluiten"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white active:bg-white/25 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Stage */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
        <AnimatePresence mode="wait">
          {isVideo ? (
            <motion.video
              key={photo.id}
              src={photo.downloadUrl}
              controls
              autoPlay
              playsInline
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="max-h-full max-w-full"
            />
          ) : (
            <motion.img
              key={photo.id}
              src={photo.url}
              alt=""
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onDoubleClick={() => setZoomed((z) => !z)}
              className={
                zoomed
                  ? 'max-h-none max-w-none scale-[1.9] cursor-zoom-out transition-transform duration-200'
                  : 'max-h-full max-w-full object-contain cursor-zoom-in transition-transform duration-200'
              }
            />
          )}
        </AnimatePresence>

        {/* Desktop / non-touch affordances. Hidden on small screens where
            swiping is the natural interaction and arrows just add clutter. */}
        {index > 0 && (
          <button
            onClick={() => goTo(index - 1)}
            aria-label="Vorige foto"
            className="absolute left-3 hidden h-12 w-12 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 sm:flex"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        {index < photos.length - 1 && (
          <button
            onClick={() => goTo(index + 1)}
            aria-label="Volgende foto"
            className="absolute right-3 hidden h-12 w-12 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 sm:flex"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* Dot indicator, only useful for a handful of photos. */}
      {photos.length > 1 && photos.length <= 12 && (
        <div className="flex shrink-0 items-center justify-center gap-1.5 pb-1">
          {photos.map((p, i) => (
            <button
              key={p.id}
              onClick={() => goTo(i)}
              aria-label={`Ga naar foto ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-5 bg-white' : 'w-1.5 bg-white/30'
              }`}
            />
          ))}
        </div>
      )}

      {/* Actions, thumb-reachable at the bottom. */}
      <div
        className="flex shrink-0 gap-3 px-4 pt-3"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <button
          onClick={handleShare}
          disabled={!!busy}
          className="flex h-14 flex-1 items-center justify-center gap-2 rounded-xl bg-white text-base font-bold text-black active:scale-[0.98] disabled:opacity-60 transition-transform"
        >
          <Share2 className="h-5 w-5" />
          {busy === 'share' ? 'Bezig…' : canShareFiles() ? 'Delen' : 'Foto opslaan'}
        </button>

        {/* When the share sheet is available, Save is the secondary action.
            Without it, Share already saves, so a second button would confuse. */}
        {canShareFiles() && (
          <button
            onClick={handleSave}
            disabled={!!busy}
            aria-label="Foto opslaan"
            className="flex h-14 w-14 items-center justify-center rounded-xl border border-white/15 bg-white/10 text-white active:scale-[0.98] disabled:opacity-60 transition-transform"
          >
            {saved ? <Check className="h-5 w-5 text-emerald-400" /> : <Download className="h-5 w-5" />}
          </button>
        )}
      </div>

      <ReportSheet
        open={reportOpen}
        scope="photo"
        eventId={eventId}
        sessionId={photo.sessionId}
        photoId={photo.id}
        onClose={() => setReportOpen(false)}
      />
    </div>
  )
}
